import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import once from 'lodash/once';
import { getCurrencyCode, getMeta } from 'modules/meta/selectors';
import { getIsRtlLanguage } from 'modules/meta/selectors';
import { getBaseMappingContextFromState } from 'modules/sapiSearch/offersMapping';
import { getNumberOfRooms, getSearchParameters } from 'modules/sapiSearch/selectors';
import { HotelOfferEntity } from 'modules/sapiSearch/slice';
import { imageProvider } from 'utils/imageProvider';
import { SmoothScrollNavigation } from '@daedalus/atlas/experimental/SmoothScrollNavigation';
import { Flex } from '@daedalus/atlas/helpers/Flex';
import { getPersistedSapiLabel } from '@daedalus/core/src/_web/sapiLabel/business';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, AnalyticsContext, Category, Entity, HotelContext, TrackEventPayload } from '@daedalus/core/src/analytics/types/Events';
import { useGetSimilarHotels } from '@daedalus/core/src/carouselWidgets/useGetSimilarHotels';
import { getHotelPrices } from '@daedalus/core/src/carouselWidgets/utils';
import { getNightlyPriceForAllRooms } from '@daedalus/core/src/price/business/price';
import { SearchState } from '@daedalus/core/src/sapi/services/searchApi';
import { SuggestionHotelCard, SuggestionHotelCardSkeleton } from '@daedalus/shared/src/search/SuggestionHotelCard';
import { Hotel } from '@findhotel/sapi';
import { getIsMultiRoomBundlesSearch, getUrlParams } from '../../../../modules/search/selectors';
const _exp = /*#__PURE__*/() => Flex.Row;
const SkeletonContainer = /*#__PURE__*/styled(_exp())({
  name: "SkeletonContainer",
  class: "snio2q4",
  propsAsIs: true
});
interface SimilarHotelsProps {
  hotelId?: string;
  placeId?: string;
  address?: string;
  rooms: string;
  checkIn: string;
  checkOut: string;
  trackingPayload: Pick<TrackEventPayload, 'component' | 'payload' | 'team'>;
  onHotelClick: ({
    hotelId
  }) => void;
  hasInsideMargin?: boolean;
  onRequestComplete?: (hotelsIds: Array<string>) => void;
  shouldWaitRequestComplete?: boolean;
  hasSidePaddings?: boolean;
  parentId?: string;
  numberOfResults?: number;
  includeSameProperty?: boolean;
}
interface SimilarHotelItemProps extends Pick<SimilarHotelsProps, 'checkIn' | 'checkOut' | 'trackingPayload' | 'hasInsideMargin'> {
  hotelEntity: Hotel;
  offerEntity: HotelOfferEntity;
  position: number;
  onClick: ({
    hotelId
  }) => void;
}
const SimilarHotelItem = ({
  hotelEntity,
  offerEntity,
  checkIn,
  checkOut,
  trackingPayload,
  hasInsideMargin,
  onClick,
  position
}: SimilarHotelItemProps) => {
  const {
    includeTaxes,
    includeLocalTaxes
  } = useSelector(getMeta);
  const currencyCode = useSelector(getCurrencyCode);
  const dispatch = useDispatch();
  const isMultiRoomBundlesSearch = useSelector(getIsMultiRoomBundlesSearch);
  const numberOfRooms = useSelector(state => getNumberOfRooms(state, hotelEntity.objectID));
  if (!hotelEntity) return null;
  const {
    hotelName,
    reviewCount,
    starRating,
    imageURIs,
    guestRating,
    objectID
  } = hotelEntity;
  const {
    price,
    anchorPrice
  } = getHotelPrices({
    offerEntity,
    checkIn,
    checkOut,
    includeTaxes,
    includeLocalTaxes
  });
  const calculatedNightlyPrice = getNightlyPriceForAllRooms(price, isMultiRoomBundlesSearch, numberOfRooms);
  const calculatedAnchorPrice = getNightlyPriceForAllRooms(anchorPrice, isMultiRoomBundlesSearch, numberOfRooms);
  const onHotelClick = () => {
    const hotelContext = {
      hotelId: objectID,
      hotelPosition: position
    } as HotelContext;
    dispatch(trackEvent({
      category: Category.User,
      entity: Entity.HotelCard,
      action: Action.Clicked,
      analyticsContext: {
        [AnalyticsContext.HotelContext]: hotelContext
      },
      ...trackingPayload
    }));
    onClick({
      hotelId: objectID
    });
  };
  return <SuggestionHotelCard key={objectID} hotelName={hotelName} starRating={starRating} reviewRating={guestRating?.overall} reviewRatingCount={reviewCount} imageUrl={imageURIs?.[0]} currency={currencyCode} price={calculatedNightlyPrice} anchorPrice={calculatedAnchorPrice} onHotelClick={onHotelClick} imageProvider={imageProvider} hasInsideMargin={hasInsideMargin} />;
};
const SuggestedHotelsSkeleton = () => <SkeletonContainer>
    {[...Array(5)].map((_, index) => <SuggestionHotelCardSkeleton key={index} />)}
  </SkeletonContainer>;
export const SimilarHotels = ({
  hotelId,
  placeId,
  address,
  rooms,
  checkIn,
  checkOut,
  trackingPayload,
  onHotelClick,
  hasInsideMargin,
  hasSidePaddings,
  onRequestComplete,
  shouldWaitRequestComplete,
  numberOfResults = 10,
  parentId,
  includeSameProperty = false
}: SimilarHotelsProps) => {
  const dispatch = useDispatch();
  const isRtlLanguage = useSelector(getIsRtlLanguage);
  const mappingContext = useSelector(getBaseMappingContextFromState);
  const {
    cugDeals,
    tier,
    metadata
  } = useSelector(getSearchParameters) || {};
  const {
    profile
  } = useSelector(getUrlParams) || {};
  const sapiLabel = getPersistedSapiLabel();
  const {
    data,
    isLoading,
    error
  } = useGetSimilarHotels({
    hotelId,
    placeId,
    address,
    checkIn,
    checkOut,
    rooms,
    tier,
    cugDeals,
    metadata,
    pageSize: numberOfResults,
    queryContext: trackingPayload,
    mappingContext,
    ...(sapiLabel && {
      label: sapiLabel
    }),
    ...(profile && {
      originId: profile
    })
  }, {
    shouldWaitRequestComplete,
    includeSameProperty
  });
  const {
    current: trackSimilarHotelsScrollOnce
  } = useRef(once(() => {
    dispatch(trackEvent({
      category: Category.User,
      entity: Entity.Carousel,
      action: Action.Scrolled,
      ...trackingPayload
    }));
  }));
  useEffect(() => {
    if (data?.searchState === SearchState.Completed) onRequestComplete?.(data.hotelIds);
  }, [data]);
  if (isLoading) {
    return <SuggestedHotelsSkeleton />;
  }
  if (error) return null;
  return <SmoothScrollNavigation isRtlLanguage={isRtlLanguage} onScroll={trackSimilarHotelsScrollOnce} hasSidePaddings={hasSidePaddings}>
      {data.hotelIds.map((hotelId, index) => <SimilarHotelItem key={`${parentId}-${hotelId}`} hotelEntity={data?.hotelEntities[hotelId]} offerEntity={data?.hotelOfferEntities[hotelId]} trackingPayload={{
      ...trackingPayload,
      payload: {
        totalNumber: data?.resultsCount,
        index: index + 1,
        searchItem: {
          hotelId,
          checkIn,
          checkOut,
          rooms
        }
      }
    }} checkIn={checkIn} checkOut={checkOut} position={index + 1} hasInsideMargin={hasInsideMargin} onClick={onHotelClick} />)}
    </SmoothScrollNavigation>;
};

require("./index.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./index.tsx");